import { useEffect, useState } from 'react';

import { AddCircleIcon, Alert02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useToast } from 'hooks/toast';
import { useSelector } from 'react-redux';

import { EmployeeConfigurationModal } from '../components/EmployeeConfigurationModal/EmployeeConfigurationModal';
import { CheckRenewalRow } from './components/CheckRenewalRow/CheckRenewalRow';
import { AddRenewalCheckModal } from 'components/AddRenewalCheckModal/AddRenewalCheckModal';
import { Button } from 'components/FormComponents';

import { useLazyGetCandidateCheckResultQuery } from 'api/candidate-checks-api';
import { useGetCandidateChecksQuery } from 'api/candidates-api';
import {
  useCreateCheckRenewalsFromConfigMutation,
  useCreateChecksRenewalsForEmployeeMutation,
  useGetCheckRenewalsForCandidateQuery,
  useRenewCheckRenewalMutation,
  useUpdateCheckRenewalMutation,
} from 'api/check-renewals-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { useConfigureEmployeeMutation } from 'api/employees-api';
import { useGetRenewalConfigsQuery } from 'api/renewals-config-api';
import { useLazyGetSubCheckResultQuery } from 'api/sub-checks-api';
import { selectPermissions } from 'api/user-permissions-slice';

import styles from './Renewals.module.scss';

export const Renewals = ({
  target,
  candidate,
  employee,
  user,
  setEmployee = () => {},
  setLoading = () => {},
}) => {
  const company = useSelector(selectCompany);
  const permissions = useSelector(selectPermissions);
  const companyChecks = useSelector(selectCompanyChecks);
  const showToast = useToast();

  const { data: renewalConfigs } = useGetRenewalConfigsQuery();
  const { data: renewalChecksData, refetch: refetchRenewalChecksData } =
    useGetCheckRenewalsForCandidateQuery({
      candidateId: candidate.id,
    });
  const {
    data: checksData,
    isLoading: isLoadingChecksData,
    refetch: refetchChecksData,
  } = useGetCandidateChecksQuery(candidate.id, {
    refetchOnMountOrArgChange: true,
  });

  const [getCandidateCheckResult] = useLazyGetCandidateCheckResultQuery();
  const [getSubCheckResult] = useLazyGetSubCheckResultQuery();
  const [createCheckRenewalsFromConfig] = useCreateCheckRenewalsFromConfigMutation();
  const [createChecksRenewalsForEmployee] = useCreateChecksRenewalsForEmployeeMutation();
  const [updateCheckRenewal] = useUpdateCheckRenewalMutation();
  const [renewCheckRenewal] = useRenewCheckRenewalMutation();
  const [configureEmployee] = useConfigureEmployeeMutation();

  const [checks, setChecks] = useState([]);
  const [renewalChecks, setRenewalChecks] = useState([]);
  const [filteredRenewalConfigs, setFilteredRenewalConfigs] = useState([]);

  const [showEmployeeConfigurationModal, setShowEmployeeConfigurationModal] = useState(false);
  const [showAddRenewalCheckModal, setShowAddRenewalCheckModal] = useState(false);

  useEffect(() => {
    if (!renewalChecksData) return;
    setRenewalChecks(renewalChecksData);
  }, [renewalChecksData]);

  useEffect(() => {
    switch (target) {
      case 'configure':
        if (employee.configured) {
          break;
        }
        setShowEmployeeConfigurationModal(true);
        break;
      default:
        break;
    }
  }, [target]);

  useEffect(() => {
    if (isLoadingChecksData) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoadingChecksData]);

  useEffect(() => {
    if (!checksData) return;

    // Group checksData by check.type
    const checks = [];
    checksData.forEach(check => {
      const typeIndex = checks.findIndex(c => c.type === check.type);

      if (typeIndex < 0) {
        const companyCheck = companyChecks.find(companyCheck => companyCheck.type == check.type);
        const checkType = companyCheck?.check_type;
        const renewalCheck = renewalChecks.find(renewalCheck => renewalCheck.type === check.type);
        const renewalConfig = renewalCheck
          ? renewalConfigs.find(
              renewalConfig => renewalConfig.id === renewalCheck.renewal_config_id,
            )
          : null;

        if (!renewalCheck && !check.renewal) {
          return;
        }

        checks.push({
          type: check.type,
          previousChecks: [],
          checkType,
          companyCheck: companyCheck,
          renewalConfig: renewalConfig,
          renewalCheck: renewalCheck,
          candidateCheck: check,
        });
      } else {
        if (check.completed_at > checks[typeIndex].candidateCheck.completed_at) {
          checks[typeIndex] = {
            ...checks[typeIndex],
            previousChecks: [...checks[typeIndex].previousChecks, checks[typeIndex].candidateCheck],
            candidateCheck: check,
          };
        } else {
          checks[typeIndex].previousChecks.push(check);
        }
      }
    });

    setChecks(checks);
  }, [checksData, renewalChecks, renewalConfigs, companyChecks]);

  useEffect(() => {
    if (!renewalConfigs) return;

    setFilteredRenewalConfigs(
      renewalConfigs.filter(
        renewalConfig =>
          !renewalChecks.find(renewalCheck => renewalCheck.renewal_config_id === renewalConfig.id),
      ),
    );
  }, [renewalConfigs, renewalChecks]);

  const onConfigure = async ({ renewal_configs = [], check_renewals = [] }) => {
    setLoading(true);
    try {
      let configured = false;
      if (renewal_configs.length > 0) {
        const { data: newRenewalsFromConfig } = await createCheckRenewalsFromConfig({
          employeeId: employee.id,
          data: { renewal_configs },
        });
        setRenewalChecks(prev => [...prev, ...newRenewalsFromConfig]);
        configured = true;
      }

      if (check_renewals.length > 0) {
        const { data: newCheckRenewals } = await createChecksRenewalsForEmployee({
          employeeId: employee.id,
          data: { check_renewals },
        });
        setRenewalChecks(prev => [...prev, ...newCheckRenewals]);
        configured = true;
      }

      await refetchChecksData();

      if (configured) {
        setEmployee({ ...employee, configured: true });
      }

      setShowEmployeeConfigurationModal(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onAddRenewalCheck = async renewalConfigs => {
    if (renewalConfigs?.length == 0) return;

    setLoading(true);
    const { data: newRenewalsFromConfig } = await createCheckRenewalsFromConfig({
      employeeId: employee.id,
      data: { renewal_configs: renewalConfigs },
    });
    setRenewalChecks(prev => [...prev, ...newRenewalsFromConfig]);
    setShowAddRenewalCheckModal(false);
    await refetchChecksData();
    setLoading(false);
  };

  const handleRenewalUpdate = async (id, data) => {
    setLoading(true);
    const { data: result, error } = await updateCheckRenewal({ id, data });
    if (error) {
      showToast('Error', 'Error updating renewal:', 'error');
    } else {
      setRenewalChecks(curr => curr.map(cr => (cr.id === id ? result : cr)));
    }
    setLoading(false);
  };

  const handleRenewNow = async id => {
    setLoading(true);
    const { data: result, error } = await renewCheckRenewal(id);
    if (error) {
      showToast('Error', 'Error renewing check: ' + error.data.error.log, 'error');
    } else {
      setRenewalChecks(curr => curr.map(cr => (cr.id === id ? result : cr)));
    }
    setLoading(false);
  };

  const handleConfigure = async () => {
    setLoading(true);
    const { data: result, error } = await configureEmployee(employee.id);
    if (error) {
      showToast('error', 'Error configuring employee: ' + error.data.error.log, 'error');
    } else if (result > 0) {
      await refetchRenewalChecksData();
      setEmployee({ ...employee, configured: true });
    } else {
      showToast('error', 'No available renewals to be configured automatically', 'error');
    }
    setLoading(false);
  };

  return (
    <div className={styles.root}>
      <EmployeeConfigurationModal
        candidateChecks={checksData}
        companyChecks={companyChecks}
        renewalConfigs={renewalConfigs}
        visible={showEmployeeConfigurationModal}
        onClose={() => setShowEmployeeConfigurationModal(false)}
        onSubmit={onConfigure}
      />
      <AddRenewalCheckModal
        visible={showAddRenewalCheckModal}
        onClose={() => setShowAddRenewalCheckModal(false)}
        companyChecks={companyChecks}
        renewalConfigs={filteredRenewalConfigs}
        onSubmit={onAddRenewalCheck}
      />
      {!employee.configured && (
        <div className="d-flex">
          <div className="card card-with-border u-padding d-flex align-items-center u-border-color--warn">
            <HugeiconsIcon
              icon={Alert02Icon}
              className="u-margin-right--small"
              size={20}
              color="#b49900"
            />
            <span>
              No ongoing checks configured. Add by configuring{' '}
              <Button
                small
                className="d-inline-flex u-margin-right--small"
                onClick={() => setShowAddRenewalCheckModal(true)}>
                Manually
              </Button>
              or
              <Button
                small
                className="d-inline-flex u-margin-left--small"
                onClick={handleConfigure}>
                Automatically
              </Button>
            </span>
          </div>
        </div>
      )}
      {employee.configured && (
        <>
          <div className="d-flex justify-content-end">
            <Button onClick={() => setShowAddRenewalCheckModal(true)}>
              <HugeiconsIcon icon={AddCircleIcon} className="u-margin-right--small" /> Add renew
            </Button>
          </div>
          {checks.map(check => (
            <CheckRenewalRow
              key={check.type}
              {...check}
              candidate={candidate}
              permissions={permissions}
              company={company}
              user={user}
              setLoading={setLoading}
              getCandidateCheckResult={getCandidateCheckResult}
              getSubCheckResult={getSubCheckResult}
              handleRenewalUpdate={handleRenewalUpdate}
              handleRenewNow={handleRenewNow}
            />
          ))}
        </>
      )}
    </div>
  );
};
